import { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

interface FAQItem {
    question: string;
    answer: string;
    questionEn: string;
    answerEn: string;
}

const FAQ = () => {
    const { t, language } = useLanguage();
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const faqData: FAQItem[] = [
        {
            question: "GenCNX คืออะไร?",
            answer: "GenCNX เป็นแพลตฟอร์มสร้างคอนเทนต์ด้วย AI ที่ช่วยให้คุณสร้างภาพ วิดีโอ และเพลงคุณภาพสูงได้ง่ายๆ เพียงแค่ใส่คำอธิบาย ระบบ AI จะสร้างผลงานที่สวยงามให้คุณภายในไม่กี่นาที",
            questionEn: "What is GenCNX?",
            answerEn: "GenCNX is an AI-powered content creation platform that helps you generate high-quality images, videos, and music easily. Simply provide a description, and our AI will create stunning results for you within minutes."
        },
        {
            question: "ระบบเครดิตทำงานอย่างไร?",
            answer: "เครดิตใช้สำหรับสร้างคอนเทนต์ แต่ละประเภทใช้เครดิตต่างกัน: ภาพ 1-2 เครดิต, วิดีโอ 5-10 เครดิต, เพลง 3-5 เครดิต คุณสามารถซื้อเครดิตเพิ่มได้ตลอดเวลา และเครดิตไม่มีวันหมดอายุ",
            questionEn: "How does the credit system work?",
            answerEn: "Credits are used to generate content. Each type uses different amounts: Images 1-2 credits, Videos 5-10 credits, Music 3-5 credits. You can purchase more credits anytime, and credits never expire."
        },
        {
            question: "ใช้เวลานานแค่ไหนในการสร้างคอนเทนต์?",
            answer: "เวลาในการสร้างขึ้นอยู่กับประเภทคอนเทนต์: ภาพใช้เวลา 30-60 วินาที, วิดีโอใช้เวลา 2-5 นาที, เพลงใช้เวลา 1-3 นาที คุณสามารถดูสถานะการสร้างได้ที่หน้า Dashboard",
            questionEn: "How long does it take to generate content?",
            answerEn: "Generation time depends on content type: Images take 30-60 seconds, Videos take 2-5 minutes, Music takes 1-3 minutes. You can check the generation status on your Dashboard."
        },
        {
            question: "ฉันสามารถใช้คอนเทนต์ที่สร้างเพื่อการค้าได้หรือไม่?",
            answer: "ได้ค่ะ! คอนเทนต์ที่คุณสร้างด้วย GenCNX เป็นของคุณ คุณสามารถนำไปใช้เพื่อการค้า โฆษณา โซเชียลมีเดีย หรือโปรเจคส่วนตัวได้ตามต้องการ โดยไม่ต้องจ่ายค่าลิขสิทธิ์เพิ่มเติม",
            questionEn: "Can I use generated content for commercial purposes?",
            answerEn: "Yes! Content you create with GenCNX is yours. You can use it for commercial purposes, advertising, social media, or personal projects without paying additional royalties."
        },
        {
            question: "ถ้าผลลัพธ์ไม่ตรงตามที่ต้องการจะทำอย่างไร?",
            answer: "คุณสามารถปรับแต่งคำอธิบาย (Prompt) ให้ละเอียดมากขึ้น หรือลองสร้างใหม่อีกครั้ง เราแนะนำให้ใช้คำอธิบายที่ชัดเจน ระบุรายละเอียดสไตล์ สี และบรรยากาศที่ต้องการ ยิ่งละเอียดมากเท่าไหร่ ผลลัพธ์จะยิ่งตรงใจมากขึ้น",
            questionEn: "What if the results don't match my expectations?",
            answerEn: "You can refine your prompt with more details or try generating again. We recommend using clear descriptions, specifying style, colors, and atmosphere. The more detailed your prompt, the better the results."
        },
        {
            question: "มีการคืนเครดิตหรือไม่?",
            answer: "ถ้าการสร้างคอนเทนต์ล้มเหลวเนื่องจากข้อผิดพลาดของระบบ เครดิตจะถูกคืนให้อัตโนมัติ คุณสามารถตรวจสอบประวัติการคืนเครดิตได้ที่หน้า Credit History",
            questionEn: "Are credits refunded?",
            answerEn: "If content generation fails due to system errors, credits will be automatically refunded. You can check your refund history on the Credit History page."
        },
        {
            question: "รองรับภาษาไทยในการสร้างคอนเทนต์หรือไม่?",
            answer: "ได้ค่ะ! ระบบรองรับภาษาไทยเต็มรูปแบบ คุณสามารถใส่คำอธิบายเป็นภาษาไทยหรือภาษาอังกฤษก็ได้ ระบบจะแปลและประมวลผลให้อัตโนมัติ",
            questionEn: "Does it support Thai language for content generation?",
            answerEn: "Yes! The system fully supports Thai language. You can provide descriptions in Thai or English, and the system will automatically translate and process them."
        },
        {
            question: "ข้อมูลและไฟล์ของฉันปลอดภัยหรือไม่?",
            answer: "ปลอดภัยแน่นอนค่ะ! เราใช้ระบบเข้ารหัส SSL/TLS สำหรับการส่งข้อมูล และเก็บไฟล์บน Cloud Storage ที่มีมาตรฐานสูง ข้อมูลส่วนตัวของคุณจะไม่ถูกนำไปใช้หรือแชร์กับบุคคลที่สาม",
            questionEn: "Is my data and files secure?",
            answerEn: "Absolutely! We use SSL/TLS encryption for data transmission and store files on high-standard Cloud Storage. Your personal data will never be used or shared with third parties."
        },
        {
            question: "สามารถยกเลิกหรือขอคืนเงินได้หรือไม่?",
            answer: "เนื่องจากเครดิตเป็นสินค้าดิจิทัลที่สามารถใช้งานได้ทันที เราจึงไม่สามารถคืนเงินได้หลังจากซื้อแล้ว แต่เครดิตไม่มีวันหมดอายุ คุณสามารถใช้งานได้ตลอดไป",
            questionEn: "Can I cancel or get a refund?",
            answerEn: "Since credits are digital goods that can be used immediately, we cannot offer refunds after purchase. However, credits never expire and you can use them anytime."
        },
        {
            question: "ติดต่อทีมสนับสนุนได้อย่างไร?",
            answer: "คุณสามารถติดต่อเราได้ผ่านอีเมล support@gencnx.com หรือใช้ระบบแชทในเว็บไซต์ ทีมงานพร้อมให้บริการทุกวันจันทร์-ศุกร์ เวลา 9:00-18:00 น.",
            questionEn: "How can I contact support?",
            answerEn: "You can contact us via email at support@gencnx.com or use the chat system on our website. Our team is available Monday-Friday, 9:00 AM - 6:00 PM."
        }
    ];

    const toggleFAQ = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5">
            {/* Header */}
            <div className="border-b border-border/50 bg-card/30 backdrop-blur-sm">
                <div className="container mx-auto px-4 py-16 text-center">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <HelpCircle className="h-12 w-12 text-primary" />
                        <Sparkles className="h-8 w-8 text-primary animate-pulse" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4 text-gradient">
                        {t("ศูนย์ช่วยเหลือ", "Help Center")}
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        {t(
                            "คำถามที่พบบ่อยและคำตอบที่จะช่วยให้คุณใช้งาน GenCNX ได้อย่างเต็มประสิทธิภาพ",
                            "Frequently asked questions and answers to help you get the most out of GenCNX"
                        )}
                    </p>
                </div>
            </div>

            {/* FAQ Content */}
            <div className="container mx-auto px-4 py-12">
                <div className="max-w-4xl mx-auto">
                    <div className="space-y-4">
                        {faqData.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-card border border-border/50 rounded-lg overflow-hidden hover:border-primary/50 transition-all duration-300"
                            >
                                <button
                                    onClick={() => toggleFAQ(index)}
                                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-accent/50 transition-colors"
                                >
                                    <span className="font-heading font-semibold text-lg pr-4">
                                        {language === "th" ? faq.question : faq.questionEn}
                                    </span>
                                    {openIndex === index ? (
                                        <ChevronUp className="h-5 w-5 text-primary flex-shrink-0" />
                                    ) : (
                                        <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                    )}
                                </button>

                                {openIndex === index && (
                                    <div className="px-6 pb-5 pt-2 border-t border-border/30 bg-accent/20">
                                        <p className="text-muted-foreground leading-relaxed">
                                            {language === "th" ? faq.answer : faq.answerEn}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <div className="mt-12 p-8 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-lg">
                        <h3 className="text-xl font-heading font-bold mb-3">
                            {t("ยังมีคำถามอื่นๆ อีกหรือไม่?", "Still have questions?")}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            {t(
                                "ทีมงานของเรายินดีให้ความช่วยเหลือคุณตลอด 24/7",
                                "Our team is happy to help you 24/7"
                            )}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a
                                href="mailto:support@gencnx.com"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                            >
                                <HelpCircle className="h-4 w-4" />
                                {t("ติดต่อฝ่ายสนับสนุน", "Contact Support")}
                            </a>
                            <Link
                                to="/dashboard"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border hover:bg-accent rounded-lg transition-colors font-medium"
                            >
                                <Sparkles className="h-4 w-4" />
                                {t("กลับไปแดชบอร์ด", "Back to Dashboard")}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FAQ;
